import React from 'react'
import { FormattedMessage } from 'react-intl'
import capitalizeString from 'utils/capitalize'
import RejectIcon from 'assets/icons/reject-icon'
import { reassessmentTriggerReasons } from 'constants/reassessmentTriggerReasons'
import Tooltip from 'components/Tooltip'
import { RedListCategoryStatus, TableNotQualifyIconWrapper } from './style'
import capitalizeFirstLetterString from 'utils/capitalizeFirstLetter'
import { specieAssessmentParameterType } from 'constants/speciesParameterType'
import {
  isQualifyingTriggerReason,
  normalizeTriggerReasonIds
} from 'utils/speciesCriteriaMet/normalizeTriggerReasonId'
import { reportCalculationsError } from './calculationsErrorHandling'
import { parseSensitiveFlag } from 'utils/sensitiveSpecies'
import { DEFAULT_SORT } from 'utils/calculationSpeciesTable/sortCalculationRows'

export const renderRedListCategoryStatus = (status) => {
  if (status == null || status === '' || status === '-') {
    return '-'
  }

  const color = {
    EX: '#FFFFFF',
    EW: 'rgba(84, 35, 68, 0.3)',
    CR: 'rgba(216, 30, 5, 0.5)',
    EN: 'rgba(252, 127, 63, 0.6)',
    VU: 'rgba(249, 232, 20, 0.6)',
    LR: 'rgba(226, 211, 105, 0.8)',
    NT: 'rgba(204, 226, 38, 0.6)',
    LC: 'rgba(96, 198, 89, 0.6)',
    DD: '#D1D1C6'
  }

  if (!color[status]) {
    return String(status)
  }

  return (
    <RedListCategoryStatus color={color[status]}>
      <FormattedMessage id={status} />
    </RedListCategoryStatus>
  )
}

const tooltipXOffset = 0
const tooltipYOffset = -16

const formattedRejectIconTooltipHint = (item) => {
  const hints = []

  for (const element of normalizeTriggerReasonIds(item)) {
    const match = reassessmentTriggerReasons.find((elementId) => elementId.id === element)
    if (match?.name) {
      hints.push(match.name)
    }
  }

  return hints
}

const notQulifyIcon = (hint) => {
  return (
    <Tooltip
      kbaCriteria={true}
      style={{ zIndex: 1000 }}
      placement='top'
      trigger={['hover']}
      align={{ offset: [tooltipXOffset, tooltipYOffset] }}
      overlay={{ label: 'nonQualifying', infoTooltip: formattedRejectIconTooltipHint(hint).join(', ') }}
    >
      <TableNotQualifyIconWrapper>
        <RejectIcon />
      </TableNotQualifyIconWrapper>
    </Tooltip>

  )
}

const getQualifies = (item, site, disabled) => {
  if (disabled) {
    return item.triggerReasonId == null ? true : isQualifyingTriggerReason(item.triggerReasonId)
  }

  return site.reassessmentFlag
    ? (item.triggerReasonId == null ? true : isQualifyingTriggerReason(item.triggerReasonId))
    : true
}

const getAssessmentParameterTypeIds = (parameters = []) => {
  return [...new Set(parameters.map((parameter) => parameter.SiteAssesmentParameterTypeId))]
}

const normalizeParameterTypeIds = (parameterTypeIds) => {
  if (parameterTypeIds == null) {
    return null
  }

  return Array.isArray(parameterTypeIds) ? parameterTypeIds : [parameterTypeIds]
}

const shouldIncludeParameterType = (item, parameterTypeId) => {
  const parameterTypeIds = normalizeParameterTypeIds(item.siteAssesmentParameterTypeId)

  if (parameterTypeIds == null) {
    return true
  }

  return parameterTypeIds.some((itemParameterTypeId) => itemParameterTypeId == parameterTypeId)
}

const formatPopulationPercent = (value) => {
  const numericValue = Number(value)

  if (Number.isNaN(numericValue) || numericValue === 0) {
    return '-'
  }

  return `${numericValue.toFixed(2)}%`
}

const getPopulationDisplay = (assess, qualifies, item) => {
  if (!qualifies) {
    return '-'
  }

  const bestSite = assess?.bestSite ?? item?.siteBest
  const bestGlobal = assess?.bestGlobal

  if (bestSite != null && bestGlobal != null) {
    const rawPopulation = (Number(bestSite) / Number(bestGlobal)) * 100
    const isInvalidPopulation = (
      rawPopulation === Number.NEGATIVE_INFINITY ||
      rawPopulation === Number.POSITIVE_INFINITY ||
      Number.isNaN(rawPopulation)
    )

    if (!isInvalidPopulation) {
      return formatPopulationPercent(rawPopulation)
    }
  }

  const apiPopulation = Number(item?.globalPopulation)
  if (!Number.isNaN(apiPopulation)) {
    return formatPopulationPercent(apiPopulation)
  }

  return '-'
}

const hasKbaCriteriaValue = (kbaCriteria) => {
  if (kbaCriteria == null) {
    return false
  }

  const normalizedValue = String(kbaCriteria).trim()
  return normalizedValue !== '' && normalizedValue !== '-'
}

const isIbaDisqualified = (item) => {
  if (typeof item?.isIbaDisqualified === 'boolean') {
    return item.isIbaDisqualified
  }

  return (
    item.disqualifiedIbaTrigger === true ||
    item.disqualifiedIbaTrigger === 1 ||
    item.disqualifiedIbaTrigger === '1' ||
    item.disqualifiedIbaTrigger === 'true' ||
    item.disqualified_iba_trigger === true ||
    item.disqualified_iba_trigger === 1 ||
    item.disqualified_iba_trigger === '1' ||
    item.disqualified_iba_trigger === 'true' ||
    item.ibaDisqualified === true ||
    item.ibaDisqualified === 1 ||
    item.ibaDisqualified === '1' ||
    item.ibaDisqualified === 'true'
  )
}

const isKbaDelisted = (item) => {
  if (typeof item?.isKbaDelisted === 'boolean') {
    return item.isKbaDelisted
  }

  if (item.triggerReasonId == null) {
    return false
  }

  return !isQualifyingTriggerReason(item.triggerReasonId)
}

const getIbaCriteriaDisplay = (item, isAves, site, canSeeSiteIBACriteria, shouldShowSiteIBAData) => {
  if (!canSeeSiteIBACriteria(site)) {
    return null
  }

  if (isIbaDisqualified(item)) {
    return notQulifyIcon(item.triggerReasonId)
  }

  const ibaValue = isAves ? capitalizeString(item.ibaCriterias) : null
  return shouldShowSiteIBAData(site, ibaValue, 'sitesManagement')
}

const parsePopulationSortValue = (population) => {
  if (population == null || population === '' || population === '-') {
    return null
  }

  const parsed = Number.parseFloat(String(population).replace('%', ''))
  return Number.isNaN(parsed) ? null : parsed
}

const formatNumericCell = (value) => {
  if (value == null || value === '' || Number.isNaN(Number(value))) {
    return '-'
  }

  return Number(value).toLocaleString()
}

const renderBooleanDisplay = (value) => {
  if (parseSensitiveFlag(value) === true) {
    return <FormattedMessage id="yes" />
  }

  if (parseSensitiveFlag(value) === false) {
    return <FormattedMessage id="no" />
  }

  return '-'
}

const makeRowObject = ({
  item,
  parameterLabel,
  key,
  qualifies,
  population,
  assess,
  isAves,
  site,
  canSeeSiteIBACriteria,
  shouldShowSiteIBAData
}) => {
  const hasCriteria = hasKbaCriteriaValue(item.kbaCriteria)
  const rowIsIbaDisqualified = isIbaDisqualified(item)
  const rowIsKbaDelisted = isKbaDelisted(item)
  const formattedKbaCriteria = hasCriteria ? `${capitalizeString(item.kbaCriteria)}` : '-'
  const shouldDisplayDelistState = rowIsKbaDelisted || !qualifies
  const shouldShowDelistIconOnBothColumns = rowIsIbaDisqualified && rowIsKbaDelisted
  const kbaCriteriaDisplay = (shouldShowDelistIconOnBothColumns || shouldDisplayDelistState)
    ? notQulifyIcon(item.triggerReasonId)
    : (hasCriteria ? formattedKbaCriteria : '-')
  const taxonGroupDisplay = `${capitalizeString(item.taxonGroup)}`
  const seasonDisplay = capitalizeString(item.season?.name || '-')
  const scientificNameDisplay = `${capitalizeFirstLetterString(item.scientificName)}`
  const commonNameDisplay = `${capitalizeFirstLetterString(item.commonName)}`
  const assessmentParameterDisplay = capitalizeFirstLetterString(parameterLabel) || '-'
  const siteBestValue = assess?.bestSite ?? item.siteBest
  const reproductiveUnitsDisplay = qualifies ? item.reproductiveUnits : '-'
  const siteBestDisplay = qualifies ? formatNumericCell(siteBestValue) : '-'

  return {
    key,
    taxonGroup: taxonGroupDisplay,
    season: seasonDisplay,
    scientificName: scientificNameDisplay,
    commonName: commonNameDisplay,
    iucnStatus: renderRedListCategoryStatus(item.iucnStatus),
    siteBest: siteBestDisplay,
    reproductiveUnits: reproductiveUnitsDisplay,
    population,
    globallySensitive: renderBooleanDisplay(item.globallySensitive),
    kbaCriteria: kbaCriteriaDisplay,
    ibaCriterias: getIbaCriteriaDisplay(
      item,
      isAves,
      site,
      canSeeSiteIBACriteria,
      shouldShowSiteIBAData
    ),
    assessmentParameter: assessmentParameterDisplay,
    sortValues: {
      taxonGroup: taxonGroupDisplay,
      season: seasonDisplay,
      scientificName: scientificNameDisplay,
      commonName: commonNameDisplay,
      iucnStatus: item.iucnStatus || '',
      assessmentParameter: assessmentParameterDisplay,
      siteBest: qualifies ? siteBestValue : null,
      reproductiveUnits: qualifies ? item.reproductiveUnits : null,
      population: parsePopulationSortValue(population),
      globallySensitive: item.globallySensitive,
      kbaCriteria: hasCriteria && !(shouldShowDelistIconOnBothColumns || shouldDisplayDelistState)
        ? formattedKbaCriteria
        : ''
    }
  }
}

const appendRowsForItem = ({
  item,
  site,
  qualifies,
  isAves,
  rows,
  existingKeys,
  canSeeSiteIBACriteria,
  shouldShowSiteIBAData
}) => {
  const matchingCriteria = site.kbaCriteria.filter(
    (criteria) => criteria.scientificName === item.scientificName
  )

  if (!matchingCriteria.length) {
    const parameterTypeIds = normalizeParameterTypeIds(item.siteAssesmentParameterTypeId) || [null]

    for (const parameterTypeId of parameterTypeIds) {
      const parameterLabel = parameterTypeId
        ? specieAssessmentParameterType.find((param) => param.id === parameterTypeId)?.name
        : null
      const key = `${item.taxonGroup}-${item.season?.name || '-'}-${item.scientificName}-${parameterLabel || 'default'}`
      if (existingKeys.has(key)) {
        continue
      }
      existingKeys.add(key)

      const population = getPopulationDisplay(null, qualifies, item)

      rows.push(makeRowObject({
        item,
        parameterLabel,
        key,
        qualifies,
        population,
        assess: null,
        isAves,
        site,
        canSeeSiteIBACriteria,
        shouldShowSiteIBAData
      }))
    }

    return
  }

  for (const criteria of matchingCriteria) {
    const parameters = criteria.speciesAssesmentParametersDisplay || []
    const assessmentParameterTypeIds = getAssessmentParameterTypeIds(parameters)

    for (const parameterTypeId of assessmentParameterTypeIds) {
      if (!shouldIncludeParameterType(item, parameterTypeId)) {
        continue
      }

      const parameterLabel = specieAssessmentParameterType.find((param) => param.id === parameterTypeId)?.name
      const key = `${item.taxonGroup}-${item.season?.name || '-'}-${item.scientificName}-${parameterLabel}`

      if (existingKeys.has(key)) {
        continue
      }
      existingKeys.add(key)

      const assess = parameters.find((param) => param.SiteAssesmentParameterTypeId === parameterTypeId)
      const population = getPopulationDisplay(assess, qualifies, item)

      rows.push(makeRowObject({
        item,
        parameterLabel,
        key,
        qualifies,
        population,
        assess,
        isAves,
        site,
        canSeeSiteIBACriteria,
        shouldShowSiteIBAData
      }))
    }
  }
}

export const makeCalculationRow = (calculationData, site, disabled, canSeeSiteIBACriteria, shouldShowSiteIBAData) => {
  const rows = []
  const existingKeys = new Set()
  const kbaCriteriaList = Array.isArray(site?.kbaCriteria) ? site.kbaCriteria : []

  if (!kbaCriteriaList.length) {
    return rows
  }

  const siteWithKbaCriteria = { ...site, kbaCriteria: kbaCriteriaList }

  for (const item of calculationData) {
    const qualifies = getQualifies(item, siteWithKbaCriteria, disabled)
    const isAves = item.taxonGroup === 'Aves'

    try {
      appendRowsForItem({
        item,
        site: siteWithKbaCriteria,
        qualifies,
        isAves,
        rows,
        existingKeys,
        canSeeSiteIBACriteria,
        shouldShowSiteIBAData
      })
    } catch (error) {
      reportCalculationsError('makeCalculationRow.item', error, {
        scientificName: item.scientificName
      })
    }
  }

  return rows
}




export const makeCalculationsColumns = (showIBAColumn = false, sortOptions = null) => {
  const renderHeader = (id) => <FormattedMessage id={id} />

  const renderSortableHeader = (id) => {
    if (!sortOptions?.onSort) {
      return renderHeader(id)
    }

    const { SortableHeaderButton, SortIndicator } = sortOptions
    const activeSort = sortOptions.sortConfig?.sortBy ? sortOptions.sortConfig : DEFAULT_SORT
    const sortIndicator = activeSort.sortBy === id
      ? (activeSort.sortDirection === 'DESC' ? '↓' : '↑')
      : ''

    return (
      <SortableHeaderButton
        type="button"
        onClick={() => sortOptions.onSort(id)}
        aria-label={`Sort by ${id}`}
      >
        <FormattedMessage id={id} />
        {sortIndicator ? <SortIndicator aria-hidden="true">{sortIndicator}</SortIndicator> : null}
      </SortableHeaderButton>
    )
  }

  const headerRenderer = sortOptions?.onSort ? renderSortableHeader : renderHeader
  const colStyle = (minWidth, whiteSpace = 'nowrap') => ({ minWidth, whiteSpace })

  const headers = [
    {
      id: 'taxonGroup',
      header: headerRenderer('taxonGroup'),
      accessorKey: 'taxonGroup',
      style: colStyle('100px'),
      cell: ({ getValue }) => getValue()
    },
    {
      id: 'season',
      header: headerRenderer('season'),
      accessorKey: 'season',
      style: colStyle('90px', 'nowrap'),
      cell: ({ getValue }) => getValue()
    },
    {
      id: 'scientificName',
      header: headerRenderer('scientificName'),
      accessorKey: 'scientificName',
      style: colStyle('180px'),
      cell: ({ getValue }) => getValue()
    },
    {
      id: 'commonName',
      header: headerRenderer('commonName'),
      accessorKey: 'commonName',
      style: colStyle('160px'),
      cell: ({ getValue }) => getValue()
    },
    {
      id: 'iucnStatus',
      header: headerRenderer('iucnStatus'),
      accessorKey: 'iucnStatus',
      style: colStyle('80px', 'nowrap'),
      cell: ({ getValue }) => getValue()
    },
    {
      id: 'assessmentParameter',
      header: headerRenderer('assessmentParameter'),
      accessorKey: 'assessmentParameter',
      style: colStyle('200px'),
      cell: ({ getValue }) => getValue()
    },
    {
      id: 'siteBest',
      header: headerRenderer('bestEstimate'),
      accessorKey: 'siteBest',
      style: colStyle('110px', 'nowrap'),
      cell: ({ getValue }) => getValue()
    },
    {
      id: 'reproductiveUnits',
      header: headerRenderer('reproductiveUnits'),
      accessorKey: 'reproductiveUnits',
      style: colStyle('100px', 'nowrap'),
      cell: ({ getValue }) => getValue()
    },
    {
      id: 'population',
      header: headerRenderer('population'),
      accessorKey: 'population',
      style: colStyle('90px', 'nowrap'),
      cell: ({ getValue }) => getValue()
    },
    {
      id: 'globallySensitive',
      header: headerRenderer('globallySensitive'),
      accessorKey: 'globallySensitive',
      style: colStyle('130px', 'nowrap'),
      cell: ({ getValue }) => getValue()
    },
    {
      id: 'kbaCriteria',
      header: headerRenderer('kbaCriteria'),
      accessorKey: 'kbaCriteria',
      style: colStyle('110px'),
      cell: ({ getValue }) => getValue()
    }
  ]

  if (showIBAColumn) {
    headers.push({
      id: 'ibaCriterias',
      header: headerRenderer('ibaCriterias'),
      accessorKey: 'ibaCriterias',
      style: colStyle('110px'),
      cell: ({ getValue }) => getValue()
    })
  }

  return headers
}
